import { Button } from '@components/common';
import { Pencil, Trash2 } from 'lucide-react';
import React from 'react';
import type {
  InventoryCategoryRecord,
  InventoryItemRecord,
  InventoryUnitRecord,
} from '@/renderer/types/inventory';

function InventoryItemStatusBadge({ isActive }: { isActive: boolean }) {
  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
        isActive
          ? 'bg-emerald-50 text-emerald-700'
          : 'bg-gray-100 text-gray-600'
      }`}
    >
      {isActive ? 'Active' : 'Inactive'}
    </span>
  );
}

function InventoryItemTypeBadge({
  itemType,
}: {
  itemType: InventoryItemRecord['item_type'];
}) {
  if (itemType !== 'supply') return null;

  return (
    <span className="inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">
      Supply
    </span>
  );
}

export function InventoryItemsTable({
  items,
  categories,
  units,
  onEdit,
  onDelete,
}: {
  items: InventoryItemRecord[];
  categories: InventoryCategoryRecord[];
  units: InventoryUnitRecord[];
  onEdit: (item: InventoryItemRecord) => void;
  onDelete: (item: InventoryItemRecord) => void;
}) {
  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
      <div className="max-h-[34rem] overflow-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 border-b border-gray-100 bg-gray-50">
            <tr className="text-left">
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Item
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Category
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Units
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Conversion
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Reorder level
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Costing
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Status
              </th>
              <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const category = categories.find(
                (candidate) => candidate.id === item.category_id,
              );
              const purchaseUnit = units.find(
                (candidate) => candidate.id === item.purchase_unit_id,
              );
              const stockUnit = units.find(
                (candidate) => candidate.id === item.stock_unit_id,
              );

              return (
                <tr key={item.id} className="border-t border-gray-100">
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">
                        {item.name}
                      </span>
                      <InventoryItemTypeBadge itemType={item.item_type} />
                    </div>
                    <div className="text-xs text-gray-500">
                      {item.sku || 'No SKU'}
                    </div>
                  </td>
                  <td className="px-5 py-4 text-gray-600">
                    {category?.name || 'Uncategorized'}
                  </td>
                  <td className="px-5 py-4 text-gray-600">
                    <div>{purchaseUnit?.name || '—'}</div>
                    <div className="text-xs text-gray-500">
                      Stock: {stockUnit?.name || '—'}
                    </div>
                  </td>
                  <td className="px-5 py-4 text-gray-600">
                    {item.conversion_rate.toLocaleString()}
                  </td>
                  <td className="px-5 py-4 text-gray-600">
                    {item.reorder_level.toLocaleString()}
                  </td>
                  <td className="px-5 py-4 text-gray-600">
                    {item.costing_method === 'weighted_average'
                      ? 'Weighted Average'
                      : item.costing_method}
                  </td>
                  <td className="px-5 py-4">
                    <InventoryItemStatusBadge isActive={item.is_active} />
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="text"
                        size="sm"
                        onClick={() => onEdit(item)}
                      >
                        <Pencil size={14} />
                        Edit
                      </Button>
                      <Button
                        variant="text"
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => onDelete(item)}
                      >
                        <Trash2 size={14} />
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
